// dNews / Templates
//
// Two-column page: left = list of saved templates with "+ New" up top.
// Right = editor for the currently-selected template (name / subject /
// Quill body). Save / Delete / "Use in Reminder" actions live in the
// editor's footer.
//
// Merge field convention: tokens like {employee_name}, {employee_email},
// {date}, {month} stay literal in the saved body. The Reminder send
// pipeline substitutes them per recipient at send time. A small hint
// strip above the body lists the supported tokens so admins don't have
// to remember them.

import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { dnAlert } from "./utils/dNews_Alert";
import { useQuill } from "react-quilljs";
import "quill/dist/quill.snow.css";
import {
    FaPlus, FaSave, FaTrashAlt, FaBell, FaTags, FaPenSquare, FaSearch, FaSortAlphaDown, FaSortAlphaUpAlt, FaTimes,
} from "react-icons/fa";
import { apiFetch } from "./utils/api";
import logo_eagle from "./assets/img/logo_eagle.png";
import "./dSpr.css";
import "./dNews_Newsletter.css";   // re-use card + label + input styles
import "./dNews_Templates.css";

const MERGE_TOKENS = [
    { token: "{employee_name}",  desc: "Recipient's full name" },
    { token: "{employee_email}", desc: "Recipient's email address" },
    { token: "{date}",           desc: "Today's date (DD Mon YYYY, IST)" },
    { token: "{month}",          desc: "Current month name" },
    { token: "{year}",           desc: "Current year" },
];

export default function DNewsTemplates({ navSize }) {
    const navigate = useNavigate();

    const [templates, setTemplates] = useState([]);
    const [selectedId, setSelectedId] = useState(null);    // null = nothing picked, "new" = unsaved draft
    const [name, setName]       = useState("");
    const [subject, setSubject] = useState("");
    const [bodyHtml, setBodyHtml] = useState(""); // body of the selected template, written into Quill via an effect
    const [busy, setBusy] = useState(false);
    const [loading, setLoading] = useState(true);
    const [tplSearch, setTplSearch] = useState("");
    const [tplAsc, setTplAsc] = useState(true);

    const { quill, quillRef } = useQuill({
        theme: "snow",
        modules: {
            toolbar: [
                [{ header: [1, 2, 3, false] }],
                ["bold", "italic", "underline", "strike"],
                [{ list: "ordered" }, { list: "bullet" }],
                [{ align: [] }],
                [{ color: [] }, { background: [] }],
                ["link"],
                ["clean"],
            ],
        },
    });

    const fetchList = useCallback(async () => {
        try {
            const res = await apiFetch("/api/dnews/templates/list", { credentials: "include" });
            if (res.status === 401 || res.status === 403) { navigate("/login"); return; }
            const data = await res.json();
            setTemplates(Array.isArray(data) ? data : []);
        } catch {
            navigate("/login");
        } finally {
            setLoading(false);
        }
    }, [navigate]);

    useEffect(() => { fetchList(); }, [fetchList]);

    // Selecting a template populates the editor. quill.setContents is the
    // safe way to write HTML - setting .root.innerHTML directly skips Quill's
    // delta tracking and breaks undo/redo.
    const selectTemplate = async (id) => {
        if (id === "new") {
            setSelectedId("new");
            setName("");
            setSubject("");
            setBodyHtml("");
            return;
        }
        try {
            const res = await apiFetch(`/api/dnews/templates/${id}`, { credentials: "include" });
            if (!res.ok) throw new Error("Failed to load template");
            const t = await res.json();
            setSelectedId(t.id);
            setName(t.name || "");
            setSubject(t.subject || "");
            // Don't write Quill here — on the very first selection the editor
            // hasn't mounted yet, so `quill` is still null and the paste is lost.
            // The effect below writes the body once Quill is ready.
            setBodyHtml(t.body_html || "");
        } catch (e) {
            dnAlert.error("Load failed", e.message);
        }
    };

    // Write the selected template's body into Quill. Fires when the selection
    // changes AND when the editor instance first becomes available — the editor
    // mounts only after a template is picked, so on the first click `quill` is
    // still null at paste time. This fixes the "first click shows an empty body
    // until you switch templates and come back" bug. User edits never re-trigger
    // it (deps are the instance + selectedId, not the live editor content).
    useEffect(() => {
        if (!quill) return;
        if (bodyHtml) {
            // dangerouslyPasteHTML (single arg) replaces the whole document so
            // saved markup — lists, alignment, colors — round-trips intact.
            quill.clipboard.dangerouslyPasteHTML(bodyHtml);
        } else {
            quill.setText("");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [quill, selectedId]);

    const validate = () => {
        if (!name.trim()) return "Name is required.";
        if (!subject.trim()) return "Subject is required.";
        const html = quill?.root?.innerHTML?.trim() || "";
        if (!html || html === "<p><br></p>") return "Body cannot be empty.";
        return null;
    };

    const onSave = async () => {
        const err = validate();
        if (err) { dnAlert.warning("Cannot save", err); return; }

        setBusy(true);
        try {
            const payload = {
                name: name.trim(),
                subject: subject.trim(),
                body_html: quill.root.innerHTML,
            };
            const isCreate = selectedId === "new";
            const url = isCreate ? "/api/dnews/templates/create" : `/api/dnews/templates/${selectedId}`;
            const res = await apiFetch(url, {
                method: isCreate ? "POST" : "PUT",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || "Save failed");

            dnAlert.success("Saved", `Template "${payload.name}" saved.`);
            await fetchList();
            // After create, jump to the just-saved row so subsequent edits
            // hit PUT instead of creating another duplicate. Sync bodyHtml first
            // so the selection-change effect re-writes the same content instead
            // of clearing the editor (bodyHtml was "" for the unsaved draft).
            if (isCreate) {
                setBodyHtml(payload.body_html);
                setSelectedId(data.id);
            }
        } catch (e) {
            dnAlert.error("Save failed", e.message);
        } finally {
            setBusy(false);
        }
    };

    const onDelete = async () => {
        if (!selectedId || selectedId === "new") return;
        const ok = await dnAlert.confirm({
            title: `Delete "${name}"?`,
            message: "This template will be gone permanently. Reminders that already used it are not affected.",
            confirmLabel: "Delete",
            danger: true,
        });
        if (!ok) return;

        setBusy(true);
        try {
            const res = await apiFetch(`/api/dnews/templates/${selectedId}`, {
                method: "DELETE", credentials: "include",
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || "Delete failed");

            dnAlert.success("Deleted");
            setSelectedId(null);
            setName(""); setSubject("");
            setBodyHtml("");
            await fetchList();
        } catch (e) {
            dnAlert.error("Delete failed", e.message);
        } finally {
            setBusy(false);
        }
    };

    // Jump straight to the Reminder page with this template pre-selected.
    // Query-string is the lightest hand-off; Reminder page will read it.
    const onUseInReminder = () => {
        if (!selectedId || selectedId === "new") return;
        navigate(`/dNews_Reminder?template=${selectedId}`);
    };

    // Insert a merge token at the current caret position in the body.
    const insertToken = (tok) => {
        if (!quill) return;
        const range = quill.getSelection(true);
        quill.insertText(range.index, tok, "user");
        quill.setSelection(range.index + tok.length, 0, "user");
        quill.focus();
    };

    const isEditing = selectedId !== null;
    const isNewDraft = selectedId === "new";

    const tq = tplSearch.trim().toLowerCase();
    const visibleTemplates = templates
        .filter((t) => !tq || `${t.name} ${t.subject || ""}`.toLowerCase().includes(tq))
        .sort((a, b) => tplAsc ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name));

    return (
        <div className="dspr-container">
            <div className={`dspr-dashboard ${navSize}`}>
                <div className="dspr-header-wrapper">
                    <h2>
                        <a className="navbar-brand" href="#"><img src={logo_eagle} alt="dAdmin Logo" style={{ height: 50, objectFit: "contain", marginLeft: -20 }} /></a>
                        dNews - Presets
                    </h2>
                    <p className="dspr-subtitle">Author reusable email templates. Reminders pull from this list - merge tokens like {"{employee_name}"} are substituted per recipient at send time.</p>
                </div>

                <div className="dspr-dashboard-content">
                    <LabelsManager />
                    <div className="dnews-tpl-layout">
                        {/* ── LEFT: List ─────────────────────────────────── */}
                        <aside className="dnews-tpl-list-card">
                            <div className="dnews-tpl-list-header">
                                <span><FaTags /> Templates ({templates.length})</span>
                                <button className="dnews-tpl-new-btn" onClick={() => selectTemplate("new")}>
                                    <FaPlus /> New
                                </button>
                            </div>
                            {/* Search + A–Z / Z–A sort */}
                            <div style={{ display: "flex", gap: 8, padding: "8px 10px", alignItems: "center" }}>
                                <div style={{ position: "relative", flex: 1 }}>
                                    <FaSearch style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#94a3b8", fontSize: 12 }} />
                                    <input type="text" className="dnews-input" placeholder="Search templates…"
                                        style={{ paddingLeft: 28, width: "100%" }}
                                        value={tplSearch} onChange={(e) => setTplSearch(e.target.value)} />
                                </div>
                                <button type="button" title={tplAsc ? "Sort Z → A" : "Sort A → Z"} onClick={() => setTplAsc(a => !a)}
                                    style={{ display: "inline-flex", alignItems: "center", gap: 5, background: "#fff", color: "#334155",
                                             border: "1px solid #e2e8f0", borderRadius: 8, padding: "8px 10px", fontSize: 12.5, cursor: "pointer", whiteSpace: "nowrap" }}>
                                    {tplAsc ? <FaSortAlphaDown /> : <FaSortAlphaUpAlt />} {tplAsc ? "A–Z" : "Z–A"}
                                </button>
                            </div>
                            <div className="dnews-tpl-list-scroll">
                                {loading ? (
                                    <div className="dnews-tpl-empty">Loading…</div>
                                ) : templates.length === 0 ? (
                                    <div className="dnews-tpl-empty">No templates yet. Click <strong>+ New</strong> to create one.</div>
                                ) : visibleTemplates.length === 0 ? (
                                    <div className="dnews-tpl-empty">No templates match your search.</div>
                                ) : visibleTemplates.map(t => (
                                    <button
                                        key={t.id}
                                        className={`dnews-tpl-list-item ${selectedId === t.id ? "active" : ""}`}
                                        onClick={() => selectTemplate(t.id)}
                                    >
                                        <div className="dnews-tpl-list-name">{t.name}</div>
                                        <div className="dnews-tpl-list-subject">{t.subject}</div>
                                        <div className="dnews-tpl-list-meta">
                                            {t.created_by_name || "-"} · updated {new Date(t.updated_at).toLocaleDateString()}
                                        </div>
                                    </button>
                                ))}
                                {/* Render the "new draft" row at the top when active */}
                                {isNewDraft && (
                                    <div className="dnews-tpl-list-item active dnews-tpl-list-draft">
                                        <div className="dnews-tpl-list-name">(unsaved draft)</div>
                                        <div className="dnews-tpl-list-meta">Editing - not saved yet</div>
                                    </div>
                                )}
                            </div>
                        </aside>

                        {/* ── RIGHT: Editor ──────────────────────────────── */}
                        <section className="dnews-card dnews-tpl-editor">
                            {!isEditing ? (
                                <div className="dnews-tpl-blank">
                                    <FaPenSquare className="dnews-tpl-blank-icon" />
                                    <h3>Pick a template to edit</h3>
                                    <p>Or click <strong>+ New</strong> on the left to start a fresh one.</p>
                                </div>
                            ) : (
                                <>
                                    <div className="dnews-row">
                                        <label className="dnews-label dnews-label-req">Template Name</label>
                                        <input
                                            type="text"
                                            className="dnews-input"
                                            value={name}
                                            onChange={(e) => setName(e.target.value)}
                                            placeholder="e.g. Monthly Timesheet Reminder"
                                            maxLength={120}
                                        />
                                    </div>

                                    <div className="dnews-row">
                                        <label className="dnews-label dnews-label-req">Subject</label>
                                        <input
                                            type="text"
                                            className="dnews-input"
                                            value={subject}
                                            onChange={(e) => setSubject(e.target.value)}
                                            placeholder="Email subject - tokens like {employee_name} are supported"
                                            maxLength={250}
                                        />
                                    </div>

                                    {/* Merge-field hint strip */}
                                    <div className="dnews-tpl-tokens">
                                        <span className="dnews-tpl-tokens-label">Insert merge field:</span>
                                        {MERGE_TOKENS.map(t => (
                                            <button
                                                key={t.token}
                                                type="button"
                                                className="dnews-tpl-token-chip"
                                                title={t.desc}
                                                onClick={() => insertToken(t.token)}
                                            >
                                                {t.token}
                                            </button>
                                        ))}
                                    </div>

                                    <div className="dnews-row">
                                        <label className="dnews-label dnews-label-req">Body</label>
                                        <div className="dnews-quill-host">
                                            <div ref={quillRef} />
                                        </div>
                                    </div>

                                    <div className="dnews-tpl-actions">
                                        {!isNewDraft && (
                                            <button
                                                type="button"
                                                className="dnews-tpl-secondary-btn"
                                                onClick={onUseInReminder}
                                                disabled={busy}
                                            >
                                                <FaBell /> Use in Reminder
                                            </button>
                                        )}
                                        {!isNewDraft && (
                                            <button
                                                type="button"
                                                className="dnews-tpl-danger-btn"
                                                onClick={onDelete}
                                                disabled={busy}
                                            >
                                                <FaTrashAlt /> Delete
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            className="dnews-submit-btn"
                                            onClick={onSave}
                                            disabled={busy}
                                        >
                                            {busy
                                                ? <><span className="dn-spinner" /> Saving…</>
                                                : <><FaSave /> {isNewDraft ? "Create template" : "Save changes"}</>}
                                        </button>
                                    </div>
                                </>
                            )}
                        </section>
                    </div>
                </div>
            </div>
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════════════════
// LABELS MANAGER - create/delete the labels used to categorise sends and scope
// unsubscribes. Lives here so labels sit alongside the templates that use them.
// ═══════════════════════════════════════════════════════════════════════════
const iconBtn = (color = "#64748b") => ({
    border: "none", background: "none", cursor: "pointer", color, display: "inline-flex", padding: 4,
});

function LabelsManager() {
    const [labels, setLabels] = useState([]);
    const [name, setName] = useState("");
    const [busy, setBusy] = useState(false);
    const [search, setSearch] = useState("");
    const [asc, setAsc] = useState(true);            // A→Z when true
    const [editId, setEditId] = useState(null);
    const [editName, setEditName] = useState("");

    const fetchAll = useCallback(async () => {
        try {
            const res = await apiFetch("/api/dnews/labels/list", { credentials: "include" });
            setLabels(await res.json());
        } catch { setLabels([]); }
    }, []);

    useEffect(() => { fetchAll(); }, [fetchAll]);

    const add = async () => {
        const n = name.trim();
        if (!n) return;
        setBusy(true);
        try {
            const res = await apiFetch("/api/dnews/labels/create", {
                method: "POST", credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ name: n }),
            });
            const d = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(d.error || "Failed");
            setName("");
            fetchAll();
        } catch (e) { dnAlert.error("Couldn't add label", e.message); }
        finally { setBusy(false); }
    };

    const saveEdit = async (l) => {
        const n = editName.trim();
        if (!n || n === l.name) { setEditId(null); return; }
        try {
            const res = await apiFetch(`/api/dnews/labels/${l.id}`, {
                method: "PUT", credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ name: n }),
            });
            if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.error || "Failed"); }
            setEditId(null);
            fetchAll();
        } catch (e) { dnAlert.error("Rename failed", e.message); }
    };

    const toggle = async (l) => {
        try {
            const res = await apiFetch(`/api/dnews/labels/${l.id}/toggle`, { method: "PATCH", credentials: "include" });
            if (!res.ok) throw new Error("Failed");
            fetchAll();
        } catch (e) { dnAlert.error("Failed", e.message); }
    };

    const remove = async (l) => {
        const ok = await dnAlert.confirm({
            title: `Delete label "${l.name}"?`,
            message: l.unsubscribed_count > 0
                ? `${l.unsubscribed_count} person(s) unsubscribed from this label. Their records are kept, but the label name is freed and can no longer be picked when sending.`
                : "It will no longer be selectable when sending.",
            confirmLabel: "Delete", danger: true,
        });
        if (!ok) return;
        try {
            const res = await apiFetch(`/api/dnews/labels/${l.id}`, { method: "DELETE", credentials: "include" });
            if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.error || "Failed"); }
            fetchAll();
        } catch (e) { dnAlert.error("Delete failed", e.message); }
    };

    const q = search.trim().toLowerCase();
    const visible = labels
        .filter((l) => !q || l.name.toLowerCase().includes(q))
        .sort((a, b) => asc ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name));

    return (
        <div className="dnews-card" style={{ marginBottom: 16, padding: "16px 18px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 8, fontWeight: 600 }}><FaTags /> Labels</span>
                <span style={{ color: "#64748b", fontSize: 13 }}>
                    - categorise a send; recipients can unsubscribe from a single label.
                </span>
            </div>

            {/* Add a label (left) + search + sort (right), one row */}
            <div style={{ display: "flex", gap: 8, marginBottom: 14, alignItems: "center", flexWrap: "wrap" }}>
                <input
                    type="text" className="dnews-input" placeholder="New label name (e.g. Weekly Infos)"
                    style={{ flex: 1, maxWidth: 440 }} value={name} maxLength={120}
                    onChange={(e) => setName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") add(); }}
                />
                <button type="button" disabled={busy || !name.trim()} onClick={add}
                    style={{
                        display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap",
                        background: "#e8520a", color: "#fff", border: "none", borderRadius: 8,
                        padding: "8px 20px", fontSize: 14, fontWeight: 600,
                        cursor: busy || !name.trim() ? "not-allowed" : "pointer",
                        opacity: busy || !name.trim() ? 0.5 : 1,
                    }}>
                    <FaPlus /> Add
                </button>
                <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
                    <div style={{ position: "relative" }}>
                        <FaSearch style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#94a3b8", fontSize: 13 }} />
                        <input type="text" className="dnews-input" placeholder="Search labels…"
                            style={{ paddingLeft: 30, width: 200 }}
                            value={search} onChange={(e) => setSearch(e.target.value)} />
                    </div>
                    <button type="button" title={asc ? "Sort Z → A" : "Sort A → Z"} onClick={() => setAsc(a => !a)}
                        style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "#fff", color: "#334155",
                                 border: "1px solid #e2e8f0", borderRadius: 8, padding: "8px 12px", fontSize: 13, cursor: "pointer" }}>
                        {asc ? <FaSortAlphaDown /> : <FaSortAlphaUpAlt />} {asc ? "A–Z" : "Z–A"}
                    </button>
                </div>
            </div>

            {visible.length === 0 ? (
                <div style={{ color: "#94a3b8", fontSize: 13 }}>
                    {labels.length === 0 ? "No labels yet. Add one above." : "No labels match your search."}
                </div>
            ) : (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                    {visible.map((l) => {
                        const on = !!l.active;
                        return (
                            <div key={l.id} style={{
                                display: "inline-flex", alignItems: "center", gap: 8, padding: "7px 12px",
                                border: "1px solid #eef2f7", borderRadius: 8, background: on ? "#fff" : "#f8fafc",
                            }}>
                                {editId === l.id ? (
                                    <>
                                        <input type="text" className="dnews-input" style={{ maxWidth: 260 }} autoFocus
                                            value={editName} maxLength={120}
                                            onChange={(e) => setEditName(e.target.value)}
                                            onKeyDown={(e) => { if (e.key === "Enter") saveEdit(l); if (e.key === "Escape") setEditId(null); }} />
                                        <button type="button" title="Save" style={iconBtn("#059669")} onClick={() => saveEdit(l)}><FaSave /></button>
                                        <button type="button" title="Cancel" style={iconBtn()} onClick={() => setEditId(null)}><FaTimes /></button>
                                    </>
                                ) : (
                                    <>
                                        <span style={{ fontSize: 14, fontWeight: 500, color: on ? "#0f172a" : "#94a3b8" }}>
                                            {l.name}
                                        </span>
                                        {l.unsubscribed_count > 0 && (
                                            <span style={{ color: "#dc2626", fontSize: 11 }}>{l.unsubscribed_count} unsub</span>
                                        )}
                                        <button type="button" onClick={() => toggle(l)}
                                            title={on ? "Disable (hide from send dropdown)" : "Enable"}
                                            style={{ border: "1px solid", borderColor: on ? "#bbf7d0" : "#e2e8f0",
                                                     background: on ? "rgba(34,197,94,.12)" : "#f1f5f9",
                                                     color: on ? "#16a34a" : "#64748b", borderRadius: 999,
                                                     padding: "3px 10px", fontSize: 11.5, fontWeight: 600, cursor: "pointer" }}>
                                            {on ? "Active" : "Disabled"}
                                        </button>
                                        <button type="button" title="Rename" style={iconBtn()}
                                            onClick={() => { setEditId(l.id); setEditName(l.name); }}><FaPenSquare /></button>
                                        <button type="button" title="Delete" style={iconBtn("#94a3b8")} onClick={() => remove(l)}><FaTrashAlt /></button>
                                    </>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
